import { brandStorage as localStorage } from '@/utils/brandSession';
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API } from '../utils/config';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { 
  Calendar, 
  Users, 
  Home, 
  Loader2,
  LogIn,
  LogOut,
  Clock,
  CalendarDays,
  CalendarRange,
  CheckCircle2,
  XCircle,
  ClipboardCheck,
  History,
  Edit,
  Send,
  Search,
  Filter,
  ArrowUpDown,
  ChevronDown,
  ChevronUp,
  RefreshCw,
  Mail,
  Upload,
  FileText,
  Download,
  AlertTriangle,
  Eye,
  Lock,
  Trash2
} from 'lucide-react';
import GuestVerificationModal from './GuestVerificationModal';
import QuesturaTimer from './QuesturaTimer';
import { SuccessPopup } from './SuccessPopup';
import { useAuth } from '../App';

// Formatta un errore Questura in righe leggibili: nome ospite + motivo SPECIFICO (italiano)
// + traduzione nella lingua dell'ospite (se disponibile). Ritorna un array di stringhe.
const formatQuesturaError = (e) => {
  if (typeof e === 'string') return [e];
  if (!e || typeof e !== 'object') return [];
  const out = [];
  const reasonIt = e.reason_it || e.detail || e.description || e.message || e.error || `Errore ${e.code || ''}`;
  if (e.guest && e.field) {
    // Errore di pre-validazione locale (campo mancante)
    out.push(`👤 ${e.guest} — ${e.field}: ${e.error || 'campo mancante'}`);
  } else if (e.guest) {
    // Errore AlloggiatiWeb arricchito (nome ospite + motivo specifico)
    out.push(`👤 ${e.guest} — ${reasonIt}`);
    if (e.reason_translated && e.reason_translated.trim() && e.reason_translated !== reasonIt) {
      out.push(`     🌐 ${e.reason_lang || 'lingua ospite'}: ${e.reason_translated}`);
    }
  } else {
    out.push(reasonIt);
  }
  return out;
};

// Calcola offset UTC per ora italiana (CET/CEST)
const getItalyOffset = (date) => {
  const y = date.getFullYear();
  const marchLast = new Date(Date.UTC(y, 2, 31));
  while (marchLast.getUTCDay() !== 0) marchLast.setUTCDate(marchLast.getUTCDate() - 1);
  marchLast.setUTCHours(1, 0, 0, 0);
  const octLast = new Date(Date.UTC(y, 9, 31));
  while (octLast.getUTCDay() !== 0) octLast.setUTCDate(octLast.getUTCDate() - 1);
  octLast.setUTCHours(1, 0, 0, 0);
  return (date >= marchLast && date < octLast) ? 120 : 60;
};

/**
 * Calcola lo stato Questura per una prenotazione e restituisce le classi CSS.
 * Verde: inviato | Arancione: in tempo (>3h) | Rosso: urgente (<3h) o scaduto
 */
const getQuesturaStatusStyle = (booking) => {
  // Già inviato → verde
  if (booking.questura_sent) {
    return { status: 'sent', borderClass: 'border-l-4 border-l-green-500', bgClass: 'bg-green-50/50', label: 'Inviato' };
  }
  // Ricevuta manuale caricata → verde (anche per immobili con AlloggiatiWeb)
  if (booking.questura_manual_receipt) {
    return { status: 'manual', borderClass: 'border-l-4 border-l-green-500', bgClass: 'bg-green-50/50', label: 'Manuale' };
  }
  
  if (!booking.check_in) return { status: 'unknown', borderClass: '', bgClass: '', label: '' };
  
  try {
    const dateStr = booking.check_in.includes('T') ? booking.check_in.split('T')[0] : booking.check_in;
    const [year, month, day] = dateStr.split('-').map(Number);
    
    // Check-in convenzionale: 14:00 ora italiana
    const checkinUTC = new Date(Date.UTC(year, month - 1, day, 14, 0));
    const offset = getItalyOffset(checkinUTC);
    const checkinTime = new Date(checkinUTC.getTime() - offset * 60000);
    
    // Deadline: 23:59 del giorno successivo ora italiana
    const deadlineDate = new Date(year, month - 1, day + 1);
    const dlStr = `${deadlineDate.getFullYear()}-${String(deadlineDate.getMonth() + 1).padStart(2, '0')}-${String(deadlineDate.getDate()).padStart(2, '0')}`;
    const [dy, dm, dd] = dlStr.split('-').map(Number);
    const deadlineUTC = new Date(Date.UTC(dy, dm - 1, dd, 23, 59));
    const dlOffset = getItalyOffset(deadlineUTC);
    const deadlineTime = new Date(deadlineUTC.getTime() - dlOffset * 60000);
    
    const now = new Date();
    
    // Check-in non ancora iniziato
    if (now < checkinTime) {
      return { status: 'future', borderClass: '', bgClass: '', label: '' };
    }
    
    const hoursLeft = (deadlineTime - now) / (1000 * 60 * 60);
    
    if (hoursLeft <= 0) {
      // Scaduto
      return { status: 'expired', borderClass: 'border-l-4 border-l-red-500', bgClass: 'bg-red-50/60', label: 'Scaduto' };
    }
    if (hoursLeft <= 3) {
      // Urgente (meno di 3 ore)
      return { status: 'urgent', borderClass: 'border-l-4 border-l-red-500', bgClass: 'bg-red-50/40', label: 'Urgente' };
    }
    // In tempo (>3 ore)
    return { status: 'pending', borderClass: 'border-l-4 border-l-orange-400', bgClass: 'bg-orange-50/40', label: 'In attesa' };
  } catch {
    return { status: 'unknown', borderClass: '', bgClass: '', label: '' };
  }
};
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { SearchableSelect } from './ui/searchable-select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';

/** Stato sintetico per il filtro: 'sent' | 'docs_not_sent' | 'no_docs' */
const getBookingDocStatus = (booking, guestDocsMap) => {
  const sent = booking.questura_sent || booking.questura_manual_receipt || booking.questura_manual_marked;
  if (sent) return 'sent';
  const docs = guestDocsMap[booking.id];
  return docs && docs.total > 0 ? 'docs_not_sent' : 'no_docs';
};

/** Intestazione colonna ordinabile (click per cambiare ordinamento) */
const SortableTh = ({ label, col, sortBy, sortOrder, onSort, align = 'left' }) => (
  <th
    onClick={() => onSort(col)}
    className={`px-4 py-3 text-${align} text-sm font-semibold text-slate-700 cursor-pointer select-none hover:bg-slate-200/60 transition-colors whitespace-nowrap`}
    data-testid={`sort-th-${col}`}
    title={`Ordina per ${label}`}
  >
    <span className={`inline-flex items-center gap-1 ${align === 'center' ? 'justify-center' : ''}`}>
      {label}
      {sortBy === col ? (
        sortOrder === 'asc' ? <ChevronUp className="h-3.5 w-3.5 text-blue-600" /> : <ChevronDown className="h-3.5 w-3.5 text-blue-600" />
      ) : (
        <ArrowUpDown className="h-3 w-3 opacity-30" />
      )}
    </span>
  </th>
);

const ResponsibleDashboard = () => {
  // Ruolo corrente: admin/ufficio (bookings_operator, customer_service) gestiscono
  // tutte le prenotazioni come i responsabili e NON sono soggetti al blocco delle 24h.
  const { user } = useAuth();
  const canManageAnytime = ['admin', 'bookings_operator', 'customer_service'].includes(user?.role);

  // View state: 'arrivals' or 'departures'
  const [activeView, setActiveView] = useState(() => {
    return localStorage.getItem('responsible_view') || 'arrivals';
  });
  
  // Period filter: 'today', 'week', 'month', 'year'
  const [period, setPeriod] = useState(() => {
    return localStorage.getItem('responsible_period') || 'today';
  });
  
  // Advanced filters (shown only for 'year' period)
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProperty, setSelectedProperty] = useState('all');
  const [propertySearch, setPropertySearch] = useState('');
  const [sortBy, setSortBy] = useState('check_in'); // 'check_in', 'check_out', 'guest_name', 'property'
  const [sortOrder, setSortOrder] = useState('asc'); // 'asc', 'desc'
  const [showFilters, setShowFilters] = useState(false);
  // Filtro stato Questura/documenti: 'all' | 'sent' | 'docs_not_sent' | 'no_docs'
  const [statusFilter, setStatusFilter] = useState('all');

  // Click su intestazione colonna: stessa colonna -> inverte ordine, altrimenti nuova colonna asc
  const handleSort = (col) => {
    if (sortBy === col) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(col);
      setSortOrder('asc');
    }
  };
  
  const [arrivals, setArrivals] = useState([]);
  const [departures, setDepartures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [properties, setProperties] = useState([]);
  const [guestDocsMap, setGuestDocsMap] = useState({});
  const [verificationBooking, setVerificationBooking] = useState(null);
  const [sendingQuestura, setSendingQuestura] = useState(null); // ID della prenotazione in invio
  const [resendingLink, setResendingLink] = useState(null); // ID della prenotazione per cui si sta reinviando il link
  const [questuraPopup, setQuesturaPopup] = useState({ open: false, title: '', message: '', type: 'success' });
  const [questuraConfirm, setQuesturaConfirm] = useState(null); // booking to confirm before sending
  const [uploadingReceipt, setUploadingReceipt] = useState(null); // booking_id in upload
  const [deletingReceipt, setDeletingReceipt] = useState(null); // booking_id in eliminazione ricevuta
  const [receiptDialogBooking, setReceiptDialogBooking] = useState(null); // booking per dialog upload
  const [markingQuestura, setMarkingQuestura] = useState(null); // booking_id in fase di marcatura
  const [postponingQuestura, setPostponingQuestura] = useState(null); // booking_id in posticipo invio
  // Paginazione/infinite-scroll: quante prenotazioni mostrare (parte da 50, +50 alla volta)
  const [visibleCount, setVisibleCount] = useState(50);
  const loadMoreRef = useRef(null);

  // Marca come già inviato alla Questura (manualmente)
  const handleMarkQuesturaSent = async (booking) => {
    try {
      setMarkingQuestura(booking.id);
      const token = localStorage.getItem('hw_token');
      await axios.patch(
        `${API}/api/bookings/${booking.id}/mark-questura-manual`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setQuesturaPopup({
        open: true,
        title: 'Marcato come inviato',
        message: `La prenotazione di ${booking.guest_name} è stata marcata come inviata alla Questura. Ricordati di caricare la ricevuta il prima possibile.`,
        type: 'warning',
        details: ['Carica la ricevuta dalla colonna "Invio Questura" per completare la procedura.'],
      });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nella marcatura');
    } finally {
      setMarkingQuestura(null);
    }
  };

  // Posticipa di 24h l'invio automatico in Questura (ospiti non ancora arrivati)
  const handlePostponeQuestura = async (booking) => {
    const count = booking.questura_postpone_count || 0;
    if (!window.confirm(`Sei sicuro/a di voler posticipare di 24 ore l'invio in Questura? (rinvio ${count + 1} di 3)\n\nOspite: ${booking.guest_name}\nAnche la data di arrivo comunicata alla Questura slitterà. L'ufficio prenotazioni riceverà una notifica.`)) return;
    try {
      setPostponingQuestura(booking.id);
      const token = localStorage.getItem('hw_token');
      const res = await axios.post(
        `${API}/api/questura/postpone/${booking.id}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Invio posticipato (${res.data.count}/3)${res.data.next_auto_send ? ` — nuovo invio previsto: ${res.data.next_auto_send}` : ''}`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nel posticipo');
    } finally {
      setPostponingQuestura(null);
    }
  };

  // Upload ricevuta manuale Questura
  const handleUploadReceipt = async (bookingId, file) => {
    if (!file) return;
    try {
      setUploadingReceipt(bookingId);
      const formData = new FormData();
      formData.append('file', file);
      const token = localStorage.getItem('hw_token');
      const res = await axios.post(
        `${API}/api/questura-manual/upload-receipt/${bookingId}`,
        formData,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
      );
      if (res.data.success) {
        toast.success('Ricevuta Questura caricata con successo');
        setReceiptDialogBooking(null);
        fetchData();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nel caricamento della ricevuta');
    } finally {
      setUploadingReceipt(null);
    }
  };

  // Scarica ricevuta manuale Questura
  const handleDownloadReceipt = async (bookingId) => {
    try {
      const token = localStorage.getItem('hw_token');
      const res = await axios.get(`${API}/api/questura-manual/receipt/${bookingId}/download`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.download_url) {
        window.open(res.data.download_url, '_blank');
      }
    } catch (err) {
      toast.error('Errore nel download della ricevuta');
    }
  };

  // Elimina ricevuta manuale Questura (caricata per errore)
  const handleDeleteReceipt = async (bookingId) => {
    if (!window.confirm('Eliminare la ricevuta Questura caricata? Potrai poi caricare quella corretta.')) return;
    try {
      setDeletingReceipt(bookingId);
      const token = localStorage.getItem('hw_token');
      await axios.delete(`${API}/api/questura-manual/receipt/${bookingId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Ricevuta eliminata. Ora puoi caricare quella corretta.');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore durante l\'eliminazione della ricevuta');
    } finally {
      setDeletingReceipt(null);
    }
  };

  // Reinvia link pre-check-in (Email + WhatsApp)
  const handleResendCheckinLink = async (booking) => {
    if (booking.checkin_link_resent) {
      toast.error('Il link è già stato reinviato per questa prenotazione');
      return;
    }
    
    try {
      setResendingLink(booking.id);
      const token = localStorage.getItem('hw_token');
      const response = await axios.post(
        `${API}/api/bookings/${booking.id}/resend-checkin-link`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        const msgs = [];
        if (response.data.email_sent) msgs.push('Email');
        if (response.data.whatsapp_sent) msgs.push('WhatsApp');
        
        if (msgs.length > 0) {
          toast.success(`Link reinviato via ${msgs.join(' e ')}!`);
        } else {
          toast.warning('Link reinviato, ma invio email/WhatsApp fallito');
        }
        
        // Aggiorna lo stato della booking nella lista locale
        setArrivals(prev => prev.map(b => 
          b.id === booking.id ? { ...b, checkin_link_resent: true } : b
        ));
        setDepartures(prev => prev.map(b => 
          b.id === booking.id ? { ...b, checkin_link_resent: true } : b
        ));
      }
    } catch (err) {
      console.error('Error resending link:', err);
      toast.error(err.response?.data?.detail || 'Errore nel reinvio del link');
    } finally {
      setResendingLink(null);
    }
  };

  // Invia alla Questura - con conferma e validazione
  const handleQuesturaConfirm = async (booking, options = {}) => {
    const { skipLocalVerification = false } = options;
    console.log('handleQuesturaConfirm chiamato per booking:', booking.id, booking.guest_name);
    
    // Blocca se già inviato o marcato come inviato manualmente
    if (booking.questura_sent) {
      toast.info('Questa prenotazione è già stata inviata alla Questura.');
      return;
    }
    if (booking.questura_manual_marked) {
      toast.info('Questa prenotazione è stata marcata come inviata manualmente. Carica la ricevuta.');
      return;
    }
    
    const docInfo = guestDocsMap[booking.id];
    const hasDocuments = docInfo && docInfo.total > 0;
    
    if (!skipLocalVerification && !hasDocuments) {
      toast.error('Nessun documento ospite inserito. Inserisci prima i dati degli ospiti.');
      return;
    }
    
    if (!skipLocalVerification && docInfo.total < booking.guests_count) {
      toast.error(`Documenti insufficienti: ${docInfo.total}/${booking.guests_count} ospiti. Inserisci tutti i documenti o indica i bambini nel modal di verifica.`);
      return;
    }
    if (!skipLocalVerification && (docInfo.incomplete || docInfo.verified < booking.guests_count)) {
      toast.error(`Verifica tutti gli ospiti prima dell'invio (${docInfo.verified}/${booking.guests_count} verificati).`);
      setVerificationBooking(booking);
      return;
    }

    // Pre-validate guest data before showing confirm dialog
    try {
      const token = localStorage.getItem('hw_token');
      const valResponse = await axios.get(
        `${API}/api/alloggiati/validate/${booking.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const valData = valResponse.data;
      if (valData.send_window?.allowed === false) {
        setQuesturaPopup({
          open: true,
          title: valData.send_window.error_code === 'ARRIVAL_IN_FUTURE'
            ? 'Invio Questura non ancora disponibile'
            : 'Finestra Questura scaduta',
          message: valData.send_window.error,
          type: 'warning',
          details: [],
        });
        return;
      }
      if (valData.errors && valData.errors.length > 0) {
        const criticalErrors = valData.errors.filter(e => 
          e.error.toLowerCase().includes('obbligatorio') || 
          e.error.toLowerCase().includes('non riconosciuto') ||
          e.error.toLowerCase().includes('errato')
        );
        
        if (criticalErrors.length > 0) {
          // Show per-guest errors
          const errorDetails = valData.errors.map(e => `${e.guest}: ${e.field} - ${e.error}`);
          setQuesturaPopup({
            open: true,
            title: 'Errori nei dati ospiti',
            message: `Trovati ${criticalErrors.length} errori che impediscono l'invio alla Questura. Correggi i dati nel modal "Verifica" e riprova.`,
            type: 'error',
            details: errorDetails,
          });
          return;
        }
        
        // Non-critical warnings - show but allow to proceed
        if (valData.errors.length > 0) {
          const warningDetails = valData.errors.map(e => `${e.guest}: ${e.field} - ${e.error}`);
          toast.warning(`Attenzione: ${valData.errors.length} avvisi sui dati ospiti. Verifica prima dell'invio.`);
        }
      }
    } catch (valErr) {
      console.warn('Validation check failed, proceeding anyway:', valErr);
    }

    // Show confirmation dialog
    setQuesturaConfirm(booking);
  };

  const isCheckInTodayInRome = (booking) => {
    const checkInDate = (booking?.check_in || '').toString().slice(0, 10);
    if (!checkInDate) return false;
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Europe/Rome', year: 'numeric', month: '2-digit', day: '2-digit'
    }).formatToParts(new Date());
    const values = Object.fromEntries(parts.map(part => [part.type, part.value]));
    return checkInDate === `${values.year}-${values.month}-${values.day}`;
  };

  const handleResponsibleVerificationCompleted = (booking) => {
    setVerificationBooking(null);
    fetchData();
    if (!booking?.questura_sent && !booking?.questura_manual_marked && isCheckInTodayInRome(booking)) {
      setTimeout(() => handleQuesturaConfirm(booking, { skipLocalVerification: true }), 300);
    }
  };

  // Invia alla Questura dopo conferma
  const handleSendQuestura = async (booking) => {
    setQuesturaConfirm(null);
    
    try {
      setSendingQuestura(booking.id);
      const token = localStorage.getItem('hw_token');
      
      const response = await axios.post(
        `${API}/api/alloggiati/submit/${booking.id}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        const receiptInfo = response.data.receipt_number 
          ? `\nNumero ricevuta: ${response.data.receipt_number}` 
          : '';
        setQuesturaPopup({
          open: true,
          title: 'Invio avvenuto correttamente!',
          message: `I dati per ${booking.guest_name} sono stati inviati e confermati dalla Questura.${receiptInfo}`,
          type: 'success',
          details: [],
        });
        fetchData();
      } else if (response.data.error_code === 'MISSING_CREDENTIALS' || response.data.error_code === 'INVALID_CREDENTIALS') {
        // Apri il dialog dedicato per credenziali mancanti O errate (rifiutate dal portale)
        setMissingCredsBooking({
          booking,
          property_id: response.data.property_id,
          property_name: response.data.property_name,
          missing: response.data.missing || [],
          can_save_credentials: response.data.can_save_credentials,
          invalid: response.data.error_code === 'INVALID_CREDENTIALS',
          reason: response.data.error || response.data.message || '',
        });
      } else {
        // Collect all error details from the backend response
        const errorDetails = [];
        const isPreflightBlock = response.data.error_code === 'MISSING_GUEST_FIELDS' || response.data.error_code === 'NO_GUESTS';
        if (response.data.validation_errors) {
          errorDetails.push(...response.data.validation_errors);
        }
        if (response.data.errors && response.data.errors.length > 0) {
          response.data.errors.forEach(e => {
            errorDetails.push(...formatQuesturaError(e));
          });
        }
        if (response.data.error_detail) {
          errorDetails.push(response.data.error_detail);
        }
        if (response.data.is_late) {
          errorDetails.push(`Check-in: ${response.data.check_in} - Ore di ritardo: ${response.data.hours_late}h`);
        }
        
        setQuesturaPopup({
          open: true,
          title: isPreflightBlock
            ? '⚠️ Anagrafiche ospiti incomplete'
            : (response.data.is_late ? 'Invio Tardivo - Oltre 24 ore' : 'Errore invio Questura'),
          message: response.data.error || response.data.message || 'Errore durante l\'invio alla Questura. Controlla i dati e riprova.',
          type: (response.data.is_late || isPreflightBlock) ? 'warning' : 'error',
          details: errorDetails,
        });
      }
    } catch (error) {
      console.error('Error sending to Questura:', error);
      const errorDetails = [];
      const errData = error.response?.data;
      if (errData) {
        if (errData.validation_errors) errorDetails.push(...errData.validation_errors);
        if (errData.errors && errData.errors.length > 0) {
          errData.errors.forEach(e => {
            errorDetails.push(...formatQuesturaError(e));
          });
        }
        if (errData.error_detail) errorDetails.push(errData.error_detail);
      }
      
      setQuesturaPopup({
        open: true,
        title: 'Errore invio Questura',
        message: errData?.error || errData?.message || errData?.detail || 'Errore di connessione con la Questura. Verifica i dati e riprova.',
        type: 'error',
        details: errorDetails,
      });
    } finally {
      setSendingQuestura(null);
    }
  };

  // Salva credenziali Questura (responsabile)
  const [missingCredsBooking, setMissingCredsBooking] = useState(null);
  const [savingCreds, setSavingCreds] = useState(false);
  const [credsForm, setCredsForm] = useState({ alloggiati_username: '', alloggiati_password: '', alloggiati_wskey: '' });

  const handleSaveCredentials = async () => {
    if (!missingCredsBooking) return;
    if (!credsForm.alloggiati_username || !credsForm.alloggiati_password) {
      toast.error('Username e Password sono obbligatori');
      return;
    }
    try {
      setSavingCreds(true);
      const token = localStorage.getItem('hw_token');
      const r = await axios.put(
        `${API}/api/properties/${missingCredsBooking.property_id}/alloggiati-credentials`,
        credsForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (r.data?.credentials_verified === false) {
        // Portale ha RIFIUTATO le nuove credenziali: dialog resta aperto per correggerle subito
        toast.error(r.data?.verify_message || 'Il portale AlloggiatiWeb rifiuta le credenziali inserite.', { duration: 12000 });
      } else {
        if (r.data?.credentials_verified === true) {
          toast.success('Credenziali salvate e VERIFICATE sul portale AlloggiatiWeb ✅');
        } else {
          toast.success(r.data?.verify_message || 'Credenziali salvate. L\'amministrazione è stata avvisata.');
        }
        setMissingCredsBooking(null);
        setCredsForm({ alloggiati_username: '', alloggiati_password: '', alloggiati_wskey: '' });
        fetchData();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nel salvataggio credenziali');
    } finally {
      setSavingCreds(false);
    }
  };

  // Save preferences to localStorage
  useEffect(() => {
    localStorage.setItem('responsible_view', activeView);
  }, [activeView]);

  useEffect(() => {
    localStorage.setItem('responsible_period', period);
  }, [period]);

  // Fetch data on mount and when period changes
  useEffect(() => {
    fetchData();
    // Segna come "visti" gli arrivi per azzerare il badge
    localStorage.setItem('hw_last_viewed_arrivi', new Date().toISOString());
  }, [period]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('hw_token');
      const headers = { Authorization: `Bearer ${token}` };

      // Calculate date range based on period
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      let startDate = new Date(today);
      let endDate = new Date(today);
      
      if (period === 'today') {
        endDate.setHours(23, 59, 59, 999);
      } else if (period === 'last48h') {
        // Ultime 48 ore - per arrivi passati che possono ancora essere gestiti
        startDate.setDate(startDate.getDate() - 2);
        endDate.setHours(23, 59, 59, 999);
      } else if (period === 'week') {
        // Settimana solare: da lunedì a domenica della settimana corrente
        const dayOfWeek = startDate.getDay(); // 0 = domenica, 1 = lunedì, etc.
        const diffToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek; // Calcola giorni per tornare a lunedì
        startDate.setDate(startDate.getDate() + diffToMonday);
        startDate.setHours(0, 0, 0, 0);
        endDate = new Date(startDate);
        endDate.setDate(endDate.getDate() + 6); // Domenica
        endDate.setHours(23, 59, 59, 999);
      } else if (period === 'month') {
        // Mese solare: dal 1° all'ultimo giorno del mese corrente
        startDate.setDate(1); // Primo del mese
        startDate.setHours(0, 0, 0, 0);
        endDate = new Date(startDate.getFullYear(), startDate.getMonth() + 1, 0); // Ultimo giorno del mese
        endDate.setHours(23, 59, 59, 999);
      } else if (period === 'year') {
        // Anno solare: dal 1 gennaio al 31 dicembre dell'anno corrente
        startDate.setMonth(0);
        startDate.setDate(1);
        startDate.setHours(0, 0, 0, 0);
        endDate.setMonth(11); // Dicembre
        endDate.setDate(31);
        endDate.setHours(23, 59, 59, 999);
      }

      // Fetch MY bookings (solo quelli dei miei immobili)
      const bookingsRes = await axios.get(`${API}/api/responsible/my-bookings`, { headers });
      const allBookings = bookingsRes.data || [];

      // Fetch MY properties to get names
      const propsRes = await axios.get(`${API}/api/responsible/my-properties`, { headers });
      setProperties(propsRes.data || []);

      // Fetch guest documents to check pre-checkin status
      try {
        const docsRes = await axios.get(`${API}/api/guest-documents/all`, { headers });
        const docs = docsRes.data || [];
        // Create a map: booking_id -> { count, verified_count, has_incomplete }
        const docsMap = {};
        docs.forEach(doc => {
          const bid = doc.booking_id;
          if (!docsMap[bid]) {
            docsMap[bid] = { total: 0, verified: 0, incomplete: false, lastSubmittedAt: null };
          }
          docsMap[bid].total++;
          if (doc.status === 'verified' || doc.status === 'sent_questura') {
            docsMap[bid].verified++;
          }
          // Le schede submitted hanno già superato la validazione al salvataggio;
          // diventano verified solo dopo il controllo esplicito del responsabile.
          if (doc.status === 'pending') {
            docsMap[bid].incomplete = true;
          }
          // Track timestamp più recente
          const docTs = doc.created_at || doc.updated_at;
          if (docTs && (!docsMap[bid].lastSubmittedAt || docTs > docsMap[bid].lastSubmittedAt)) {
            docsMap[bid].lastSubmittedAt = docTs;
          }
        });
        setGuestDocsMap(docsMap);
      } catch (e) {
        console.log('Could not fetch guest documents');
      }

      // Helper: verifica se una prenotazione è annullata (gestisce tutti i possibili formati)
      const isCancelled = (b) => {
        const c = b.cancelled;
        const s = (b.status || '').toLowerCase();
        return c === true || c === 'true' || c === 'True' || c === 1 || c === '1' || c === 'si' || c === 'yes' || s === 'cancelled';
      };

      // Filter arrivals (check_in within period) - ESCLUDI le annullate
      const filteredArrivals = allBookings.filter(b => {
        if (isCancelled(b)) return false;
        const checkIn = new Date(b.check_in);
        return checkIn >= startDate && checkIn <= endDate;
      }).sort((a, b) => new Date(a.check_in) - new Date(b.check_in));

      // Filter departures (check_out within period) - ESCLUDI le annullate
      const filteredDepartures = allBookings.filter(b => {
        if (isCancelled(b)) return false;
        const checkOut = new Date(b.check_out);
        return checkOut >= startDate && checkOut <= endDate;
      }).sort((a, b) => new Date(a.check_out) - new Date(b.check_out));

      setArrivals(filteredArrivals);
      setDepartures(filteredDepartures);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getPropertyName = (booking) => {
    if (booking.property_id) {
      const prop = properties.find(p => p.id === booking.property_id);
      if (prop) return prop.name;
    }
    // Extract from booking_code
    if (booking.booking_code) {
      const code = booking.booking_code.split('/')[0];
      const prop = properties.find(p => booking.booking_code.startsWith(p.property_code));
      if (prop) return prop.name;
      return code;
    }
    return 'N/A';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('it-IT', { 
      weekday: 'short',
      day: '2-digit', 
      month: 'short'
    });
  };

  const getPeriodLabel = () => {
    const now = new Date();
    const monthNames = ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 
                        'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];
    
    if (period === 'today') return 'Oggi';
    if (period === 'last48h') return 'Ultime 48 ore';
    if (period === 'week') {
      // Mostra la settimana corrente (es: "Settimana 4-10 Marzo")
      const dayOfWeek = now.getDay();
      const diffToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
      const monday = new Date(now);
      monday.setDate(monday.getDate() + diffToMonday);
      const sunday = new Date(monday);
      sunday.setDate(sunday.getDate() + 6);
      return `Settimana ${monday.getDate()}-${sunday.getDate()} ${monthNames[monday.getMonth()]}`;
    }
    if (period === 'month') return `${monthNames[now.getMonth()]} ${now.getFullYear()}`;
    if (period === 'year') return `Anno ${now.getFullYear()}`;
    return '';
  };

  // Apply filters and sorting to data
  const getFilteredAndSortedData = (data) => {
    let filtered = [...data];
    
    // Apply search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(b => 
        (b.guest_name || '').toLowerCase().includes(search) ||
        (b.guest_phone || '').includes(search) ||
        (b.guest_email || '').toLowerCase().includes(search) ||
        (b.booking_code || '').toLowerCase().includes(search)
      );
    }
    
    // Apply property filter
    if (selectedProperty && selectedProperty !== 'all') {
      filtered = filtered.filter(b => b.property_id === selectedProperty);
    }

    // Apply status filter (inviati / con documenti non inviati / senza documenti)
    if (statusFilter !== 'all') {
      filtered = filtered.filter(b => getBookingDocStatus(b, guestDocsMap) === statusFilter);
    }
    
    // Apply sorting
    filtered.sort((a, b) => {
      let valA, valB;
      
      switch (sortBy) {
        case 'check_in':
          valA = new Date(a.check_in);
          valB = new Date(b.check_in);
          break;
        case 'check_out':
          valA = new Date(a.check_out);
          valB = new Date(b.check_out);
          break;
        case 'guest_name':
          valA = (a.guest_name || '').toLowerCase();
          valB = (b.guest_name || '').toLowerCase();
          break;
        case 'property':
          const propA = properties.find(p => p.id === a.property_id);
          const propB = properties.find(p => p.id === b.property_id);
          valA = (propA?.name || '').toLowerCase();
          valB = (propB?.name || '').toLowerCase();
          break;
        case 'guests_count':
          valA = a.guests_count || 0;
          valB = b.guests_count || 0;
          break;
        case 'nights': {
          const nightsOf = (x) => Math.ceil((new Date(x.check_out) - new Date(x.check_in)) / 86400000) || 0;
          valA = nightsOf(a);
          valB = nightsOf(b);
          break;
        }
        case 'docs':
          valA = guestDocsMap[a.id]?.total || 0;
          valB = guestDocsMap[b.id]?.total || 0;
          break;
        case 'questura': {
          // Rank: scaduto < urgente < in attesa < futuro < inviato
          const rank = (x) => {
            if (x.questura_sent || x.questura_manual_receipt || x.questura_manual_marked) return 4;
            const st = getQuesturaStatusStyle(x).status;
            return { expired: 0, urgent: 1, pending: 2, future: 3 }[st] ?? 3;
          };
          valA = rank(a);
          valB = rank(b);
          break;
        }
        case 'receipt': {
          const hasReceipt = (x) => (x.questura_receipt_id || x.receipt_downloaded || x.questura_manual_receipt) ? 1 : 0;
          valA = hasReceipt(a);
          valB = hasReceipt(b);
          break;
        }
        default:
          valA = new Date(a.check_in);
          valB = new Date(b.check_in);
      }
      
      if (sortOrder === 'asc') {
        return valA > valB ? 1 : valA < valB ? -1 : 0;
      } else {
        return valA < valB ? 1 : valA > valB ? -1 : 0;
      }
    });
    
    return filtered;
  };

  const rawData = activeView === 'arrivals' ? arrivals : departures;
  const currentData = getFilteredAndSortedData(rawData);
  const hasMore = currentData.length > visibleCount;

  // Reset del numero visibile quando cambiano periodo / vista / filtri
  useEffect(() => {
    setVisibleCount(50);
  }, [period, activeView, searchTerm, selectedProperty, statusFilter, sortBy, sortOrder]);

  // Infinite scroll (mobile): carica altri 50 quando il sentinel entra in viewport
  useEffect(() => {
    if (!hasMore) return;
    const el = loadMoreRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => { if (entries[0].isIntersecting) setVisibleCount((c) => c + 50); },
      { rootMargin: '300px' }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [hasMore, visibleCount, currentData.length]);


  return (
    <div className="p-4 pt-14 md:pt-6 md:p-6 space-y-4 md:space-y-6" data-testid="responsible-dashboard">
      {/* Header - Mobile Optimized */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-slate-800" data-testid="manager-dashboard-title">Dashboard Gestore</h1>
          <p className="text-sm text-slate-500">{getPeriodLabel()}</p>
        </div>
        
        {/* Period Filter - five stable columns on mobile, compact row on desktop */}
        <div className="grid grid-cols-5 sm:flex items-center gap-1 bg-slate-100 rounded-lg p-1 w-full sm:w-auto" data-testid="period-filter">
          <Button
            variant={period === 'last48h' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setPeriod('last48h')}
            className={`min-w-0 px-1 sm:px-3 whitespace-nowrap text-[11px] sm:text-xs md:text-sm ${period === 'last48h' ? 'bg-purple-600 text-white' : ''}`}
            data-testid="period-filter-last-48-hours"
          >
            <History className="hidden sm:block h-3 w-3 md:h-4 md:w-4 mr-1" />
            48h
          </Button>
          <Button
            variant={period === 'today' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setPeriod('today')}
            className={`min-w-0 px-1 sm:px-3 whitespace-nowrap text-[11px] sm:text-xs md:text-sm ${period === 'today' ? 'bg-blue-600 text-white' : ''}`}
            data-testid="period-filter-today"
          >
            <Clock className="hidden sm:block h-3 w-3 md:h-4 md:w-4 mr-1" />
            Oggi
          </Button>
          <Button
            variant={period === 'week' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setPeriod('week')}
            className={`min-w-0 px-1 sm:px-3 whitespace-nowrap text-[11px] sm:text-xs md:text-sm ${period === 'week' ? 'bg-blue-600 text-white' : ''}`}
            data-testid="period-filter-week"
          >
            <CalendarDays className="hidden sm:block h-3 w-3 md:h-4 md:w-4 mr-1" />
            <span className="sm:hidden">Sett.</span><span className="hidden sm:inline">Settimana</span>
          </Button>
          <Button
            variant={period === 'month' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setPeriod('month')}
            className={`min-w-0 px-1 sm:px-3 whitespace-nowrap text-[11px] sm:text-xs md:text-sm ${period === 'month' ? 'bg-blue-600 text-white' : ''}`}
            data-testid="period-filter-month"
          >
            <CalendarRange className="hidden sm:block h-3 w-3 md:h-4 md:w-4 mr-1" />
            Mese
          </Button>
          <Button
            variant={period === 'year' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setPeriod('year')}
            className={`min-w-0 px-1 sm:px-3 whitespace-nowrap text-[11px] sm:text-xs md:text-sm ${period === 'year' ? 'bg-indigo-600 text-white' : ''}`}
            data-testid="period-filter-year"
          >
            <Calendar className="hidden sm:block h-3 w-3 md:h-4 md:w-4 mr-1" />
            Anno
          </Button>
        </div>
      </div>

      {/* View Toggle Buttons - Full width on mobile */}
      <div className="flex gap-2 md:gap-4" data-testid="dashboard-stats">
        <Button
          onClick={() => setActiveView('arrivals')}
          className={`flex-1 h-12 md:h-16 text-sm md:text-lg ${
            activeView === 'arrivals' 
              ? 'bg-green-600 hover:bg-green-700 text-white' 
              : 'bg-white border-2 border-green-600 text-green-600 hover:bg-green-50'
          }`}
          data-testid="btn-arrivals"
        >
          <LogIn className="h-4 w-4 md:h-6 md:w-6 mr-1 md:mr-3" />
          <span className="hidden sm:inline">Arrivi</span> ({currentData.length}{rawData.length !== currentData.length ? `/${rawData.length}` : ''})
        </Button>
        <Button
          onClick={() => setActiveView('departures')}
          className={`flex-1 h-12 md:h-16 text-sm md:text-lg ${
            activeView === 'departures' 
              ? 'bg-orange-600 hover:bg-orange-700 text-white' 
              : 'bg-white border-2 border-orange-600 text-orange-600 hover:bg-orange-50'
          }`}
          data-testid="btn-departures"
        >
          <LogOut className="h-4 w-4 md:h-6 md:w-6 mr-1 md:mr-3" />
          <span className="hidden sm:inline">Partenze</span> ({currentData.length}{rawData.length !== currentData.length ? `/${rawData.length}` : ''})
        </Button>
      </div>

      {/* Advanced Filters - Show for all periods */}
      <Card className="p-4" data-testid="filters-section">
        {/* Filtro stato Questura/documenti - sempre visibile */}
        {(() => {
          const counts = { sent: 0, docs_not_sent: 0, no_docs: 0 };
          rawData.forEach(b => { counts[getBookingDocStatus(b, guestDocsMap)]++; });
          const chips = [
            { key: 'all', label: 'Tutte', count: rawData.length, active: 'bg-slate-700 text-white border-slate-700', dot: null },
            { key: 'sent', label: 'Inviate', count: counts.sent, active: 'bg-green-600 text-white border-green-600', dot: 'bg-green-500' },
            { key: 'docs_not_sent', label: 'Con documenti, da inviare', count: counts.docs_not_sent, active: 'bg-orange-500 text-white border-orange-500', dot: 'bg-orange-400' },
            { key: 'no_docs', label: 'Senza documenti', count: counts.no_docs, active: 'bg-red-600 text-white border-red-600', dot: 'bg-red-500' },
          ];
          return (
            <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="status-filter-chips">
              {chips.map(c => (
                <button
                  key={c.key}
                  onClick={() => setStatusFilter(c.key)}
                  data-testid={`status-chip-${c.key}`}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs md:text-sm font-medium border transition-colors ${
                    statusFilter === c.key ? c.active : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'
                  }`}
                >
                  {c.dot && <span className={`h-2 w-2 rounded-full ${statusFilter === c.key ? 'bg-white' : c.dot}`} />}
                  {c.label} ({c.count})
                </button>
              ))}
            </div>
          );
        })()}
        <div className="flex items-center justify-between mb-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2"
          >
            <Filter className="h-4 w-4" />
            {showFilters ? 'Nascondi Filtri' : 'Mostra Filtri'}
            {showFilters ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </Button>
          {(searchTerm || selectedProperty !== 'all' || statusFilter !== 'all') && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setSearchTerm('');
                setSelectedProperty('all');
                setStatusFilter('all');
              }}
            >
              Reset Filtri
            </Button>
          )}
        </div>
        
        {showFilters && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Cerca nome, telefono, email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            
            {/* Property Filter (con ricerca) */}
            <Select value={selectedProperty} onValueChange={setSelectedProperty}>
              <SelectTrigger data-testid="property-filter-trigger">
                <SelectValue placeholder="Tutti gli immobili" />
              </SelectTrigger>
              <SelectContent>
                <div className="px-2 py-2 sticky top-0 bg-white z-10 border-b">
                  <div className="relative">
                    <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      data-testid="property-filter-search"
                      placeholder="Cerca immobile..."
                      value={propertySearch}
                      onChange={(e) => setPropertySearch(e.target.value)}
                      onKeyDown={(e) => e.stopPropagation()}
                      className="pl-8 h-8"
                    />
                  </div>
                </div>
                <SelectItem value="all">Tutti gli immobili</SelectItem>
                {properties
                  .filter(p => (p.name || '').toLowerCase().includes(propertySearch.toLowerCase()))
                  .map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                {properties.filter(p => (p.name || '').toLowerCase().includes(propertySearch.toLowerCase())).length === 0 && (
                  <div className="px-3 py-2 text-sm text-slate-400">Nessun immobile trovato</div>
                )}
              </SelectContent>
            </Select>
            
            {/* Sort By */}
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger>
                <SelectValue placeholder="Ordina per..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="check_in">Data Arrivo</SelectItem>
                <SelectItem value="check_out">Data Partenza</SelectItem>
                <SelectItem value="guest_name">Nome Ospite</SelectItem>
                <SelectItem value="property">Immobile</SelectItem>
                <SelectItem value="guests_count">N. Ospiti</SelectItem>
                <SelectItem value="nights">Notti</SelectItem>
                <SelectItem value="docs">Documenti</SelectItem>
                <SelectItem value="questura">Stato Questura</SelectItem>
                <SelectItem value="receipt">Ricevuta</SelectItem>
              </SelectContent>
            </Select>
            
            {/* Sort Order */}
            <Button
              variant="outline"
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              className="flex items-center gap-2"
            >
              <ArrowUpDown className="h-4 w-4" />
              {sortOrder === 'asc' ? 'Crescente' : 'Decrescente'}
            </Button>
          </div>
        )}
      </Card>

      {/* Legend - Compact on mobile */}
      <div className="flex flex-wrap items-center justify-center gap-3 md:gap-6 text-xs md:text-sm text-slate-600 bg-slate-50 rounded-lg p-2 md:p-3">
        <span className="font-medium hidden sm:inline">Legenda:</span>
        <div className="flex items-center gap-1">
          <CheckCircle2 className="h-4 w-4 md:h-5 md:w-5 text-green-500" />
          <span>Completo</span>
        </div>
        <div className="flex items-center gap-1">
          <CheckCircle2 className="h-4 w-4 md:h-5 md:w-5 text-red-500" />
          <span>Incompleto</span>
        </div>
        <div className="flex items-center gap-1">
          <XCircle className="h-4 w-4 md:h-5 md:w-5 text-red-500" />
          <span>Non effettuato</span>
        </div>
        <span className="hidden sm:inline text-slate-300">|</span>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded-sm bg-green-500" />
          <span>Questura OK</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded-sm bg-orange-400" />
          <span>In attesa</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded-sm bg-red-500" />
          <span>Urgente/Scaduto</span>
        </div>
      </div>

      {/* Data Table */}
      <Card className="overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
            <span className="ml-3 text-slate-600">Caricamento...</span>
          </div>
        ) : currentData.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-slate-500">
            <Calendar className="h-16 w-16 text-slate-300 mb-4" />
            <p className="text-xl font-medium">
              Nessun {activeView === 'arrivals' ? 'arrivo' : 'partenza'} {getPeriodLabel().toLowerCase()}
            </p>
            <p className="text-sm mt-2">
              Cambia il periodo per vedere più prenotazioni
            </p>
          </div>
        ) : (
          <>
            {/* Mobile Card View */}
            <div className="md:hidden space-y-3">
              {currentData.slice(0, visibleCount).map((booking, idx) => {
                const checkIn = new Date(booking.check_in);
                const checkOut = new Date(booking.check_out);
                const nights = Math.ceil((checkOut - checkIn) / (1000 * 60 * 60 * 24));
                const isArrivalToday = new Date(booking.check_in).toDateString() === new Date().toDateString();
                const isDepartureToday = new Date(booking.check_out).toDateString() === new Date().toDateString();
                const docInfo = guestDocsMap[booking.id];
                const hasDocuments = docInfo && docInfo.total > 0;
                const isComplete = hasDocuments && !docInfo.incomplete && docInfo.verified >= docInfo.total;
                const isIncomplete = hasDocuments && (docInfo.incomplete || docInfo.verified < docInfo.total);
                const expectedGuests = booking.guests_count || 1;
                const incompleteLabel = docInfo?.total >= expectedGuests
                  ? `Verifica ${docInfo.total}/${expectedGuests}`
                  : `Completa ${docInfo?.total || 0}/${expectedGuests}`;
                const verifiedLabel = `Verificati ${docInfo?.verified || 0}/${expectedGuests}`;
                const qStyle = getQuesturaStatusStyle(booking);
                const canSend = hasDocuments && docInfo && !docInfo.incomplete
                  && docInfo.total >= (booking.guests_count || 1)
                  && docInfo.verified >= (booking.guests_count || 1);
                
                return (
                  <div 
                    key={booking.id || idx} 
                    className={`rounded-lg border p-4 ${qStyle.borderClass} ${
                      booking.cancelled 
                        ? 'border-red-400 bg-red-50 opacity-75' 
                        : qStyle.bgClass || (isArrivalToday || isDepartureToday 
                          ? 'border-yellow-400 bg-yellow-50' 
                          : 'bg-white border-slate-200')
                    }`}
                    data-testid={`booking-card-${booking.id || idx}`}
                  >
                    {/* Guest Name & Status */}
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <p className={`font-bold ${booking.cancelled ? 'text-red-600 line-through' : 'text-slate-800'}`}>{booking.guest_name || 'N/A'}</p>
                        <p className="text-xs text-slate-500">{getPropertyName(booking)}</p>
                        {booking.booking_code && booking.booking_code.trim() && (
                          <p className="text-xs text-slate-400 font-mono" data-testid={`booking-code-${booking.id || idx}`}>
                            N° {booking.booking_code.trim()}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {booking.cancelled ? (
                          <Badge className="bg-red-600 text-white text-xs font-bold" data-testid={`badge-cancelled-${booking.id || idx}`}>ANNULLATA</Badge>
                        ) : booking.questura_sent ? (
                          <>
                            <CheckCircle2 className="h-5 w-5 text-green-500" />
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setVerificationBooking(booking)}
                              className="h-8 px-2 text-xs border-green-500 text-green-700"
                            >
                              <Eye className="h-3 w-3 mr-1" />
                              Visualizza
                            </Button>
                          </>
                        ) : (
                          <>
                            {!hasDocuments && (
                          <>
                            <XCircle className="h-5 w-5 text-red-500" />
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setVerificationBooking(booking)}
                              className="h-8 px-2 text-xs border-red-500 text-red-700"
                            >
                              <Edit className="h-3 w-3 mr-1" />
                              Inserisci
                            </Button>
                          </>
                        )}
                        {isIncomplete && (
                          <>
                            <CheckCircle2 className="h-5 w-5 text-red-500" />
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setVerificationBooking(booking)}
                              className="h-8 px-2 text-xs border-orange-500 text-orange-700"
                            >
                              <Edit className="h-3 w-3 mr-1" />
                              {incompleteLabel}
                            </Button>
                          </>
                        )}
                        {isComplete && (
                          <>
                            <CheckCircle2 className="h-5 w-5 text-green-500" />
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setVerificationBooking(booking)}
                              className="h-8 px-2 text-xs border-green-500 text-green-700"
                            >
                              <ClipboardCheck className="h-3 w-3 mr-1" />
                              {verifiedLabel}
                            </Button>
                          </>
                        )}
                          </>
                        )}
                      </div>
                    </div>
                    
                    {/* Dates */}
                    <div className="flex gap-4 text-sm mb-2">
                      <div>
                        <span className="text-slate-500">Arrivo: </span>
                        <span className="font-medium">{formatDate(booking.check_in)}</span>
                        {isArrivalToday && <Badge className="ml-1 bg-green-100 text-green-800 text-xs">OGGI</Badge>}
                      </div>
                      <div>
                        <span className="text-slate-500">Partenza: </span>
                        <span className="font-medium">{formatDate(booking.check_out)}</span>
                        {isDepartureToday && <Badge className="ml-1 bg-orange-100 text-orange-800 text-xs">OGGI</Badge>}
                      </div>
                    </div>
                    
                    {/* Info Row */}
                    <div className="flex items-center gap-4 text-sm text-slate-600">
                      <div className="flex items-center gap-1">
                        <Users className="h-4 w-4" />
                        <span>{booking.guests_count || 1}</span>
                      </div>
                      <div>
                        <span>{nights} notti</span>
                      </div>
                      {/* Data inserimento documenti */}
                      {docInfo && docInfo.lastSubmittedAt && (
                        <span className="text-[10px] text-slate-400" title={`Ultimo doc inserito: ${new Date(docInfo.lastSubmittedAt).toLocaleString('it-IT')}`}>
                          Doc: {new Date(docInfo.lastSubmittedAt).toLocaleDateString('it-IT', {day:'2-digit', month:'2-digit'})} {new Date(docInfo.lastSubmittedAt).toLocaleTimeString('it-IT', {hour:'2-digit', minute:'2-digit'})}
                        </span>
                      )}
                      {/* Stato Questura */}
                      <div className="ml-auto">
                        <QuesturaTimer 
                          checkInDate={booking.check_in} 
                          questuraSent={booking.questura_sent || booking.questura_manual_receipt}
                          compact={true}
                        />
                      </div>
                    </div>
                    
                    {/* Contact Buttons */}
                    <div className="flex gap-2 mt-3 pt-3 border-t">
                      {booking.guest_phone && (
                        <a 
                          href={`https://wa.me/${booking.guest_phone.replace(/[^0-9]/g, '')}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex-1 text-center py-2 px-3 bg-green-50 text-green-700 rounded-lg text-sm font-medium"
                        >
                          📱 WhatsApp
                        </a>
                      )}
                      {booking.guest_email && (
                        <a 
                          href={`mailto:${booking.guest_email}`}
                          className="flex-1 text-center py-2 px-3 bg-blue-50 text-blue-700 rounded-lg text-sm font-medium"
                        >
                          ✉ Email
                        </a>
                      )}
                    </div>
                    
                    {/* Questura Manuale - Mobile */}
                    {!booking.questura_sent && !booking.cancelled && (
                      <div className="flex flex-col gap-2 mt-2">
                        {booking.questura_manual_receipt ? (
                          <div className="flex gap-2">
                            <div className="flex-1 text-center py-2 px-3 bg-blue-50 text-blue-700 rounded-lg text-sm font-medium cursor-pointer"
                                 onClick={() => handleDownloadReceipt(booking.id)}>
                              <FileText className="h-4 w-4 inline mr-1" /> Ricevuta Manuale
                            </div>
                            {canManageAnytime && (
                              <button
                                type="button"
                                title="Elimina ricevuta (caricata per errore)"
                                className="h-9 px-3 bg-red-50 text-red-600 rounded-lg text-sm font-medium disabled:opacity-50"
                                onClick={() => handleDeleteReceipt(booking.id)}
                                disabled={deletingReceipt === booking.id}
                                data-testid={`delete-receipt-mobile-${booking.id}`}
                              >
                                {deletingReceipt === booking.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                              </button>
                            )}
                          </div>
                        ) : (
                          <>
                            {/* Pulsante Invia Questura (automatico) - MOBILE.
                                Stesse condizioni della tabella desktop: documenti presenti,
                                non già marcato manuale, non scaduto (o admin/ufficio). */}
                            {hasDocuments && !booking.questura_manual_marked && (qStyle.status !== 'expired' || canManageAnytime) && (
                              <Button
                                variant="outline"
                                size="sm"
                                className={`w-full h-9 text-xs font-semibold ${canSend ? 'border-purple-500 text-purple-700 hover:bg-purple-50' : 'border-slate-300 text-slate-400 cursor-not-allowed'}`}
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleQuesturaConfirm(booking);
                                }}
                                disabled={!canSend || sendingQuestura === booking.id}
                                data-testid={`questura-btn-mobile-${booking.id}`}
                                title={!canSend ? `Documenti insufficienti (${docInfo?.total || 0}/${booking.guests_count || 1})` : 'Invia alla Questura'}
                              >
                                {sendingQuestura === booking.id ? (
                                  <Loader2 className="h-3 w-3 animate-spin mr-1" />
                                ) : (
                                  <Send className="h-3 w-3 mr-1" />
                                )}
                                Invia Questura
                              </Button>
                            )}
                            <Button
                              variant="outline"
                              size="sm"
                              className="w-full h-9 text-xs border-orange-300 text-orange-700 hover:bg-orange-50"
                              onClick={() => setReceiptDialogBooking(booking)}
                              disabled={uploadingReceipt === booking.id}
                              data-testid={`upload-receipt-mobile-${booking.id}`}
                            >
                              {uploadingReceipt === booking.id ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Upload className="h-3 w-3 mr-1" />} Carica Ricevuta
                            </Button>
                            {/* Posticipa invio 24h - MOBILE */}
                            {!booking.questura_manual_marked && (
                              (booking.questura_postpone_count || 0) >= 3 ? (
                                <div className="w-full text-center text-[11px] text-slate-400 italic" data-testid={`postpone-limit-mobile-${booking.id}`}>
                                  Limite rinvii raggiunto (3/3)
                                </div>
                              ) : (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="w-full h-9 text-xs border-amber-500 text-amber-700 hover:bg-amber-50"
                                  onClick={() => handlePostponeQuestura(booking)}
                                  disabled={postponingQuestura === booking.id}
                                  data-testid={`postpone-questura-mobile-${booking.id}`}
                                >
                                  {postponingQuestura === booking.id ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Clock className="h-3 w-3 mr-1" />}
                                  Posticipa invio 24h{(booking.questura_postpone_count || 0) > 0 ? ` (${booking.questura_postpone_count}/3)` : ''}
                                </Button>
                              )
                            )}
                            {!booking.has_questura && !booking.questura_manual_marked && (
                              <button
                                type="button"
                                className="w-full h-8 text-[11px] text-slate-500 hover:text-slate-700 border border-dashed border-slate-300 rounded-md italic"
                                onClick={() => {
                                  if (window.confirm(`Confermi di aver GIÀ inviato manualmente alla Questura i dati di ${booking.guest_name}?\n\nQuesto bloccherà i tentativi automatici. Solo l'amministrazione può poi annullare questa operazione.`)) {
                                    handleMarkQuesturaSent(booking);
                                  }
                                }}
                                disabled={markingQuestura === booking.id}
                                data-testid={`mark-questura-mobile-${booking.id}`}
                                title="Segna invio manuale (richiede conferma)"
                              >
                                {markingQuestura === booking.id ? (
                                  <span className="inline-flex items-center justify-center w-full"><Loader2 className="h-3 w-3 animate-spin mr-1" />…</span>
                                ) : (
                                  'Inviato a mano? Segnalo'
                                )}
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Mobile: carica altre prenotazioni (infinite scroll + pulsante) */}
            <div className="md:hidden">
              {hasMore ? (
                <div ref={loadMoreRef} className="flex justify-center py-4">
                  <Button
                    variant="outline"
                    onClick={() => setVisibleCount((c) => c + 50)}
                    data-testid="btn-load-more-mobile"
                  >
                    Carica altre ({currentData.length - visibleCount} rimanenti)
                  </Button>
                </div>
              ) : currentData.length > 50 ? (
                <p className="text-center text-xs text-slate-400 py-3">
                  Tutte le {currentData.length} prenotazioni mostrate
                </p>
              ) : null}
            </div>

            {/* Desktop Table View */}
            <div className="hidden md:block overflow-auto max-h-[60vh]">
            <table className="w-full">
              <thead className={`sticky top-0 z-10 ${activeView === 'arrivals' ? 'bg-green-50' : 'bg-orange-50'}`}>
                <tr>
                  <SortableTh label="Arrivo" col="check_in" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
                  <SortableTh label="Partenza" col="check_out" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
                  <SortableTh label="Ospite" col="guest_name" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
                  <SortableTh label="Immobile" col="property" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
                  <SortableTh label="Ospiti" col="guests_count" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
                  <SortableTh label="Notti" col="nights" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
                  <SortableTh label="Pre Check-in" col="docs" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} align="center" />
                  <th className="px-4 py-3 text-center text-sm font-semibold text-slate-700">Timer 24h</th>
                  <SortableTh label="Questura" col="questura" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} align="center" />
                  <SortableTh label="Ricevuta" col="receipt" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} align="center" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {currentData.slice(0, visibleCount).map((booking, idx) => {
                  const checkIn = new Date(booking.check_in);
                  const checkOut = new Date(booking.check_out);
                  const nights = Math.ceil((checkOut - checkIn) / (1000 * 60 * 60 * 24));
                  const isArrivalToday = new Date(booking.check_in).toDateString() === new Date().toDateString();
                  const isDepartureToday = new Date(booking.check_out).toDateString() === new Date().toDateString();
                  const isToday = activeView === 'arrivals' ? isArrivalToday : isDepartureToday;
                  const qStyle = getQuesturaStatusStyle(booking);
                  
                  return (
                    <tr 
                      key={booking.id || idx} 
                      className={`hover:bg-slate-50/80 ${qStyle.borderClass} ${
                        booking.cancelled 
                          ? 'bg-red-50 opacity-75' 
                          : qStyle.bgClass || (isToday ? 'bg-yellow-50' : '')
                      }`}
                      data-testid={`booking-row-${booking.id || idx}`}
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span className={`font-medium ${booking.cancelled ? 'text-red-500' : ''}`}>{formatDate(booking.check_in)}</span>
                          {isArrivalToday && !booking.cancelled && (
                            <Badge className="bg-green-100 text-green-800 text-xs">OGGI</Badge>
                          )}
                          {booking.cancelled && (
                            <Badge className="bg-red-600 text-white text-xs font-bold" data-testid={`badge-cancelled-desktop-${booking.id || idx}`}>ANNULLATA</Badge>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span className={`font-medium ${booking.cancelled ? 'text-red-500' : ''}`}>{formatDate(booking.check_out)}</span>
                          {isDepartureToday && !booking.cancelled && (
                            <Badge className="bg-orange-100 text-orange-800 text-xs">OGGI</Badge>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div>
                          <p className={`font-medium ${booking.cancelled ? 'text-red-600 line-through' : 'text-slate-800'}`}>{booking.guest_name || 'N/A'}</p>
                          {booking.guest_email && (
                            <a 
                              href={`mailto:${booking.guest_email}`}
                              className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                            >
                              <span>✉</span> {booking.guest_email}
                            </a>
                          )}
                          {booking.guest_phone && (
                            <a 
                              href={`https://wa.me/${booking.guest_phone.replace(/[^0-9]/g, '')}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-green-600 hover:underline flex items-center gap-1"
                            >
                              <span>📱</span> {booking.guest_phone}
                            </a>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <Home className="h-4 w-4 text-slate-400" />
                          <div className="leading-tight">
                            <span className="text-sm">{getPropertyName(booking)}</span>
                            {booking.booking_code && booking.booking_code.trim() && (
                              <span className="block text-xs text-slate-400 font-mono">N° {booking.booking_code.trim()}</span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          <Users className="h-4 w-4 text-slate-400" />
                          <span>{booking.guests_count || 1}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="font-medium">{nights}</span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        {(() => {
                          const docInfo = guestDocsMap[booking.id];
                          const hasDocuments = docInfo && docInfo.total > 0;
                          const isComplete = hasDocuments && !docInfo.incomplete && docInfo.verified >= docInfo.total;
                          const isIncomplete = hasDocuments && (docInfo.incomplete || docInfo.verified < docInfo.total);
                          const expectedGuests = booking.guests_count || 1;
                          const incompleteLabel = docInfo?.total >= expectedGuests
                            ? `Verifica ${docInfo.total}/${expectedGuests}`
                            : `Completa ${docInfo?.total || 0}/${expectedGuests}`;
                          const verifiedLabel = `Verificati ${docInfo?.verified || 0}/${expectedGuests}`;
                          const canSend = hasDocuments && !docInfo.incomplete
                            && docInfo.total >= (booking.guests_count || 1)
                            && docInfo.verified >= (booking.guests_count || 1);
                          
                          // Se questura_sent: tutto cristallizzato, solo visualizzazione
                          const questuraDone = booking.questura_sent;
                          
                          return (
                            <div className="flex items-center justify-center gap-2 flex-wrap">
                              {questuraDone ? (
                                <>
                                  <CheckCircle2 className="h-6 w-6 text-green-500" />
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setVerificationBooking(booking)}
                                    className="h-7 px-2 text-xs border-green-500 text-green-700 hover:bg-green-50"
                                    data-testid={`view-btn-${booking.id}`}
                                  >
                                    <Eye className="h-3 w-3 mr-1" />
                                    Visualizza
                                  </Button>
                                  <Lock className="h-3.5 w-3.5 text-green-500" title="Inviato alla Questura" />
                                </>
                              ) : (
                                <>
                                  {!hasDocuments && (
                                    <>
                                      <XCircle className="h-6 w-6 text-red-500" />
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => setVerificationBooking(booking)}
                                        className="h-7 px-2 text-xs border-red-500 text-red-700 hover:bg-red-50"
                                        data-testid={`insert-btn-${booking.id}`}
                                      >
                                        <Edit className="h-3 w-3 mr-1" />
                                        Inserisci
                                      </Button>
                                      {!booking.has_questura && (
                                        <span className="text-[9px] text-red-600 font-medium" title="Documenti e ricevuta Questura obbligatori per questo immobile">
                                          Richiesti!
                                        </span>
                                      )}
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          handleResendCheckinLink(booking);
                                        }}
                                        disabled={resendingLink === booking.id}
                                        className="h-7 px-2 text-xs border-blue-500 text-blue-700 hover:bg-blue-50"
                                        data-testid={`resend-link-btn-${booking.id}`}
                                        title="Reinvia link pre-check-in via Email e WhatsApp (disponibile finché i dati non sono inviati alla Questura)"
                                      >
                                        {resendingLink === booking.id ? (
                                          <Loader2 className="h-3 w-3 animate-spin" />
                                        ) : (
                                          <RefreshCw className="h-3 w-3 mr-1" />
                                        )}
                                        Reinvia Link{booking.checkin_link_resent_count > 0 ? ` (${booking.checkin_link_resent_count})` : ''}
                                      </Button>
                                    </>
                                  )}
                                  {isIncomplete && (
                                    <>
                                      <CheckCircle2 className="h-6 w-6 text-orange-500" />
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => setVerificationBooking(booking)}
                                        className="h-7 px-2 text-xs border-orange-500 text-orange-700 hover:bg-orange-50"
                                        data-testid={`complete-btn-${booking.id}`}
                                      >
                                        <Edit className="h-3 w-3 mr-1" />
                                        {incompleteLabel}
                                      </Button>
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          handleResendCheckinLink(booking);
                                        }}
                                        disabled={resendingLink === booking.id}
                                        className="h-7 px-2 text-xs border-blue-500 text-blue-700 hover:bg-blue-50"
                                        data-testid={`resend-link-btn-incomplete-${booking.id}`}
                                        title="Reinvia link pre-check-in via Email e WhatsApp (disponibile finché i dati non sono inviati alla Questura)"
                                      >
                                        {resendingLink === booking.id ? (
                                          <Loader2 className="h-3 w-3 animate-spin" />
                                        ) : (
                                          <RefreshCw className="h-3 w-3 mr-1" />
                                        )}
                                        Reinvia{booking.checkin_link_resent_count > 0 ? ` (${booking.checkin_link_resent_count})` : ''}
                                      </Button>
                                    </>
                                  )}
                                  {isComplete && (
                                    <>
                                      <CheckCircle2 className="h-6 w-6 text-green-500" />
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => setVerificationBooking(booking)}
                                        className="h-7 px-2 text-xs border-green-500 text-green-700 hover:bg-green-50"
                                        data-testid={`verify-btn-${booking.id}`}
                                      >
                                        <ClipboardCheck className="h-3 w-3 mr-1" />
                                        {verifiedLabel}
                                      </Button>
                                    </>
                                  )}
                              {/* Pulsante Invia Questura - nascosto se già inviato o marcato manualmente.
                                  Per admin/ufficio (canManageAnytime) resta visibile anche oltre le 24h (scaduta). */}
                              {hasDocuments && !booking.questura_sent && !booking.questura_manual_marked && (qStyle.status !== 'expired' || canManageAnytime) && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleQuesturaConfirm(booking);
                                  }}
                                  disabled={!canSend || sendingQuestura === booking.id}
                                  className={`h-7 px-2 text-xs ${canSend ? 'border-purple-500 text-purple-700 hover:bg-purple-50' : 'border-slate-300 text-slate-400 cursor-not-allowed'}`}
                                  data-testid={`questura-btn-${booking.id}`}
                                  title={!canSend ? `Documenti insufficienti (${docInfo.total}/${booking.guests_count || 1})` : 'Invia alla Questura'}
                                >
                                  {sendingQuestura === booking.id ? (
                                    <Loader2 className="h-3 w-3 animate-spin" />
                                  ) : (
                                    <Send className="h-3 w-3 mr-1" />
                                  )}
                                  Questura
                                </Button>
                              )}
                              {/* Posticipa invio 24h: ospiti non ancora arrivati */}
                              {!booking.questura_sent && !booking.questura_manual_marked && !booking.questura_manual_receipt && !booking.cancelled && (
                                (booking.questura_postpone_count || 0) >= 3 ? (
                                  <span className="text-[10px] text-slate-400 italic whitespace-nowrap" title="Massimo 3 rinvii di 24 ore" data-testid={`postpone-limit-${booking.id}`}>
                                    Limite rinvii (3/3)
                                  </span>
                                ) : (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      handlePostponeQuestura(booking);
                                    }}
                                    disabled={postponingQuestura === booking.id}
                                    className="h-7 px-2 text-xs border-amber-500 text-amber-700 hover:bg-amber-50"
                                    data-testid={`postpone-questura-btn-${booking.id}`}
                                    title="Ospiti non ancora arrivati? Posticipa di 24 ore l'invio in Questura"
                                  >
                                    {postponingQuestura === booking.id ? (
                                      <Loader2 className="h-3 w-3 animate-spin" />
                                    ) : (
                                      <Clock className="h-3 w-3 mr-1" />
                                    )}
                                    Posticipa{(booking.questura_postpone_count || 0) > 0 ? ` (${booking.questura_postpone_count}/3)` : ''}
                                  </Button>
                                )
                              )}
                                </>
                              )}
                            </div>
                          );
                        })()}
                      </td>
                      
                      {/* Colonna Stato Questura */}
                      <td className="px-4 py-3 text-center">
                        <QuesturaTimer 
                          checkInDate={booking.check_in} 
                          questuraSent={booking.questura_sent || booking.questura_manual_receipt}
                          compact={true}
                        />
                      </td>
                      
                      {/* Colonna Invio Questura */}
                      <td className="px-4 py-3 text-center">
                        {booking.questura_sent ? (
                          <div className="flex items-center justify-center gap-1" title={`Inviato automaticamente: ${booking.questura_sent_at ? new Date(booking.questura_sent_at).toLocaleString('it-IT') : 'N/A'}`}>
                            <CheckCircle2 className="h-5 w-5 text-green-500" />
                          </div>
                        ) : booking.questura_manual_receipt ? (
                          <div className="flex items-center justify-center gap-1" title={`Questura OK — ricevuta manuale caricata${booking.questura_manual_receipt_file ? ': ' + booking.questura_manual_receipt_file : ''}`}>
                            <CheckCircle2 className="h-5 w-5 text-green-500" />
                            <span className="text-[9px] text-green-600 font-medium">Manuale</span>
                          </div>
                        ) : !booking.has_questura && booking.questura_manual_marked ? (
                          <div className="flex items-center justify-center gap-1" title="Segnata come inviata manualmente — carica la ricevuta dalla colonna Ricevuta">
                            <Clock className="h-5 w-5 text-orange-400" />
                            <span className="text-[9px] text-orange-600 font-medium">Attesa ric.</span>
                          </div>
                        ) : !booking.has_questura ? (
                          <div className="flex flex-col items-center gap-1">
                            <span className="text-[10px] text-slate-400 italic">Inviato a mano?</span>
                            <button
                              type="button"
                              className="text-[10px] text-slate-500 hover:text-slate-700 underline decoration-dotted underline-offset-2"
                              onClick={() => {
                                if (window.confirm(`Confermi di aver GIÀ inviato manualmente alla Questura i dati di ${booking.guest_name}?\n\nQuesto bloccherà i tentativi automatici. Solo l'amministrazione può poi annullare questa operazione.`)) {
                                  handleMarkQuesturaSent(booking);
                                }
                              }}
                              disabled={markingQuestura === booking.id}
                              data-testid={`mark-questura-sent-${booking.id}`}
                              title="Segna invio manuale (richiede conferma)"
                            >
                              {markingQuestura === booking.id ? (
                                <span className="inline-flex items-center"><Loader2 className="h-3 w-3 animate-spin mr-1" />…</span>
                              ) : (
                                'segna invio manuale'
                              )}
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center justify-center gap-1" title="Non inviato">
                            <XCircle className="h-5 w-5 text-slate-300" />
                          </div>
                        )}
                      </td>
                      
                      {/* Colonna Ricevuta */}
                      <td className="px-4 py-3 text-center">
                        {(booking.questura_receipt_id || booking.receipt_downloaded) ? (
                          <div className="flex items-center justify-center gap-1" title={booking.questura_receipt_id ? `Ricevuta: ${booking.questura_receipt_id}` : 'Ricevuta scaricata'}>
                            <CheckCircle2 className="h-5 w-5 text-green-500" />
                          </div>
                        ) : booking.questura_manual_receipt ? (
                          <div className="flex items-center justify-center gap-1.5">
                            <button type="button"
                               className="cursor-pointer"
                               title="Scarica ricevuta caricata manualmente"
                               onClick={() => handleDownloadReceipt(booking.id)}>
                              <Download className="h-5 w-5 text-blue-500 hover:text-blue-700" />
                            </button>
                            {canManageAnytime && (
                              <button
                                type="button"
                                title="Elimina ricevuta (caricata per errore)"
                                className="text-red-400 hover:text-red-600 disabled:opacity-50"
                                onClick={(e) => { e.stopPropagation(); handleDeleteReceipt(booking.id); }}
                                disabled={deletingReceipt === booking.id}
                                data-testid={`delete-receipt-ricevuta-${booking.id}`}
                              >
                                {deletingReceipt === booking.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                              </button>
                            )}
                          </div>
                        ) : booking.questura_sent ? (
                          <div className="flex items-center justify-center gap-1" title="In attesa di ricevuta (scaricabile domani alle 8:00)">
                            <Clock className="h-5 w-5 text-orange-400 animate-pulse" />
                          </div>
                        ) : (
                          <button
                            type="button"
                            className="inline-flex items-center gap-1 h-7 px-2 rounded-md border border-orange-300 text-orange-700 hover:bg-orange-50 text-[10px] font-medium disabled:opacity-50"
                            title="Carica la ricevuta della Questura (PDF o immagine)"
                            onClick={(e) => { e.stopPropagation(); setReceiptDialogBooking(booking); }}
                            disabled={uploadingReceipt === booking.id}
                            data-testid={`upload-receipt-ricevuta-${booking.id}`}
                          >
                            {uploadingReceipt === booking.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}
                            Carica
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {hasMore && (
              <div className="px-4 py-3 bg-slate-50 text-center">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setVisibleCount((c) => c + 50)}
                  data-testid="btn-load-more-desktop"
                >
                  Carica altre ({currentData.length - visibleCount} rimanenti)
                </Button>
              </div>
            )}
            {!hasMore && currentData.length > 50 && (
              <div className="px-4 py-3 bg-slate-50 text-center text-xs text-slate-400">
                Tutte le {currentData.length} prenotazioni mostrate
              </div>
            )}
            </div>
          </>
        )}
      </Card>

      {/* Guest Verification Modal */}
      {verificationBooking && (
        <GuestVerificationModal
          booking={verificationBooking}
          onClose={() => setVerificationBooking(null)}
          onVerified={() => handleResponsibleVerificationCompleted(verificationBooking)}
        />
      )}

      {/* Questura Confirmation Dialog */}
      {questuraConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" data-testid="questura-confirm-overlay">
          <div className="bg-white rounded-xl shadow-2xl p-6 max-w-md mx-4" data-testid="questura-confirm-dialog">
            <div className="flex items-center gap-3 mb-4">
              <div className="rounded-full bg-purple-100 p-2">
                <Send className="h-6 w-6 text-purple-600" />
              </div>
              <h3 className="text-lg font-bold text-slate-800">Vuoi inviare ora alla Questura?</h3>
            </div>
            <p className="text-sm text-slate-600 mb-1">
              Gli ospiti sono stati salvati e verificati. Stai per effettuare l'invio reale ad AlloggiatiWeb per:
            </p>
            <p className="font-semibold text-slate-800 mb-4">{questuraConfirm.guest_name}</p>
            <div className="flex items-center gap-2 rounded-md border border-green-200 bg-green-50 px-3 py-2 mb-4" data-testid="questura-all-guests-selected">
              <CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0" />
              <span className="text-sm font-medium text-green-800">
                Tutti gli ospiti verificati sono selezionati per l'invio
                {questuraConfirm.guests_count ? ` (${questuraConfirm.guests_count}/${questuraConfirm.guests_count})` : ''}
              </span>
            </div>
            <p className="text-xs text-slate-500 mb-6">
              Verifica che tutti i documenti degli ospiti siano corretti prima di procedere. Questa operazione non può essere annullata.
            </p>
            <div className="flex gap-3 justify-end">
              <Button variant="outline" onClick={() => setQuesturaConfirm(null)} data-testid="questura-confirm-cancel">
                Annulla
              </Button>
              <Button 
                onClick={() => handleSendQuestura(questuraConfirm)} 
                className="bg-purple-600 hover:bg-purple-700"
                data-testid="questura-confirm-send"
              >
                <Send className="h-4 w-4 mr-2" />
                Invia ora alla Questura
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Questura Success/Error Popup */}
      <SuccessPopup 
        open={questuraPopup.open} 
        onClose={() => setQuesturaPopup(p => ({...p, open: false}))} 
        title={questuraPopup.title} 
        message={questuraPopup.message} 
        type={questuraPopup.type}
        details={questuraPopup.details || []}
      />

      {/* Dialog Upload Ricevuta Manuale Questura */}
      <Dialog open={!!receiptDialogBooking} onOpenChange={() => setReceiptDialogBooking(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg">
              <Upload className="h-5 w-5 text-orange-600" />
              Carica Ricevuta Questura
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="text-sm text-slate-600">
              <p className="font-medium mb-1">Prenotazione: {receiptDialogBooking?.guest_name}</p>
              <p className="text-xs text-slate-500">Immobile: {receiptDialogBooking?.property_name}</p>
            </div>
            
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <div className="flex gap-2 items-start">
                <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-amber-800 leading-relaxed">
                  Si ricorda che per questo immobile la comunicazione alla Questura deve essere effettuata manualmente.
                  La mancata trasmissione dei dati degli alloggiati e la mancata raccolta dei documenti degli ospiti 
                  comportano responsabilità diretta in caso di verifiche o sanzioni da parte delle autorità competenti, 
                  ai sensi dell'art. 109 T.U.L.P.S.
                </p>
              </div>
            </div>
            
            <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center hover:border-orange-400 transition-colors">
              <input
                type="file"
                id="receipt-file-input"
                className="hidden"
                onChange={(e) => {
                  if (e.target.files[0] && receiptDialogBooking) {
                    handleUploadReceipt(receiptDialogBooking.id, e.target.files[0]);
                  }
                }}
                data-testid="receipt-file-input"
              />
              <label htmlFor="receipt-file-input" className="cursor-pointer">
                {uploadingReceipt ? (
                  <div className="flex flex-col items-center gap-2">
                    <Loader2 className="h-8 w-8 text-orange-500 animate-spin" />
                    <span className="text-sm text-slate-600">Caricamento in corso...</span>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-2">
                    <Upload className="h-8 w-8 text-slate-400" />
                    <span className="text-sm text-slate-600">Clicca per selezionare il file</span>
                    <span className="text-xs text-slate-400">PDF, immagini o qualsiasi altro formato (max 20MB)</span>
                  </div>
                )}
              </label>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      {/* Dialog Credenziali Questura mancanti */}
      <Dialog open={!!missingCredsBooking} onOpenChange={() => { setMissingCredsBooking(null); setCredsForm({ alloggiati_username: '', alloggiati_password: '', alloggiati_wskey: '' }); }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg text-red-700">
              <AlertTriangle className="h-5 w-5" />
              {missingCredsBooking?.invalid ? 'Credenziali Questura ERRATE' : 'Credenziali Questura mancanti'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="text-sm text-slate-700">
              {missingCredsBooking?.invalid ? (
                <>
                  <p className="font-medium">Il portale AlloggiatiWeb ha <span className="text-red-600 font-bold">RIFIUTATO</span> le credenziali di <strong>{missingCredsBooking?.property_name}</strong>.</p>
                  {missingCredsBooking?.reason && (
                    <p className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-md p-2 mt-2 whitespace-pre-wrap" data-testid="invalid-creds-reason">{missingCredsBooking.reason}</p>
                  )}
                  <p className="text-xs text-slate-500 mt-1">Inserisci le credenziali corrette: verranno <strong>verificate subito</strong> sul portale.</p>
                </>
              ) : (
                <>
                  <p className="font-medium">Per inviare i dati alla Questura serve configurare le credenziali AlloggiatiWeb di <strong>{missingCredsBooking?.property_name}</strong>.</p>
                  <p className="text-xs text-slate-500 mt-1">L'ufficio HW Smart è già stato avvisato via email.</p>
                </>
              )}
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <div className="flex gap-2 items-start">
                <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
                <div className="text-xs text-amber-800 leading-relaxed space-y-1">
                  <p><strong>Non hai le credenziali?</strong> Devi inviare i dati a mano sul portale Questura, scaricare la ricevuta PDF e caricarla qui col pulsante <em>Carica Ricevuta</em>. L'ufficio si occuperà di seguirti.</p>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium text-slate-700 mb-1 block">Username AlloggiatiWeb <span className="text-red-500">*</span></label>
                <Input
                  value={credsForm.alloggiati_username}
                  onChange={(e) => setCredsForm({ ...credsForm, alloggiati_username: e.target.value })}
                  placeholder="Username del portale Polizia di Stato"
                  data-testid="alloggiati-username-input"
                  autoComplete="off"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-700 mb-1 block">Password AlloggiatiWeb <span className="text-red-500">*</span></label>
                <Input
                  type="password"
                  value={credsForm.alloggiati_password}
                  onChange={(e) => setCredsForm({ ...credsForm, alloggiati_password: e.target.value })}
                  placeholder="Password del portale"
                  data-testid="alloggiati-password-input"
                  autoComplete="new-password"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-700 mb-1 block">WSKey <span className="text-slate-400">(facoltativo)</span></label>
                <Input
                  value={credsForm.alloggiati_wskey}
                  onChange={(e) => setCredsForm({ ...credsForm, alloggiati_wskey: e.target.value })}
                  placeholder="Lascia vuoto se non la conosci"
                  data-testid="alloggiati-wskey-input"
                  autoComplete="off"
                />
                <p className="text-[11px] text-slate-500 mt-1 leading-relaxed">
                  La <strong>WSKey</strong> (Web Service Key) si recupera dal portale Polizia di Stato → menu <em>Web Service</em> → <em>Genera Chiave</em>. Se non sai come fare lascia vuoto: la inserirà <strong>l'amministrazione HW</strong> per te.
                </p>
              </div>
            </div>

            <div className="flex gap-3 justify-end pt-2">
              <Button
                variant="outline"
                onClick={() => { setMissingCredsBooking(null); setCredsForm({ alloggiati_username: '', alloggiati_password: '', alloggiati_wskey: '' }); }}
                data-testid="missing-creds-cancel"
              >
                Annulla
              </Button>
              <Button
                onClick={handleSaveCredentials}
                disabled={savingCreds || !credsForm.alloggiati_username || !credsForm.alloggiati_password}
                className="bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="missing-creds-save"
              >
                {savingCreds ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : null}
                Salva Credenziali
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ResponsibleDashboard;
